import React from "react";
import { decimalPoints, remoteStatus } from "./constants";
import { colors } from "./colors";
import { scaleLinear } from "d3-scale";
import {
  CloudDoubleIconLocalVersion,
  CloudDoubleIconCloudVersion,
  CloudDoubleIconDone,
  CloudDoubleIconWarning,
} from "./customIcons";
import CloudIcon from "@material-ui/icons/Cloud";
import ComputerIcon from "@material-ui/icons/Computer";
import Tooltip from "@material-ui/core/Tooltip";

///////////////////////////////////
/// This file contains various functions that are used by multiple files. Instead of rewriting the function in each file, the function are here and can be imported to any file
///////////////////////////////////

// Calculate percentage
export const percentage = (number, required) => {
  if (number < required) {
    return (number / required) * 100;
  } else {
    return 100;
  }
};

// Get symmetric coordinates when creating symmetric locations on the map
export const getSymmetricCoordinates = (coordArray) => {
  let symmetricCoords;
  if (coordArray.length === 3) {
    symmetricCoords = [coordArray[0], 100 - coordArray[1], coordArray[2]];
  } else {
    symmetricCoords = coordArray.map((coord, index) => {
      if (index % 2 === 0) {
        return 100 - coord;
      } else {
        return coord;
      }
    });
  }
  return symmetricCoords;
};

// Scroll in the location list and sample list.
// Automatically scrolls when the auto-advance jumps to a new location or a new sample is acquired that is pass the screen limit
export const scrollIntoView = (element, scrollDiv) => {
  // console.log("ScrollIntoView");
  const elementHeight = element.offsetHeight;
  const scrollDivHeight = scrollDiv.offsetHeight;
  const distFromTop = element.offsetTop;
  const scrollDist = scrollDiv.scrollTop;

  // if selected item is out of sight (above)
  if (
    distFromTop + elementHeight - scrollDist > scrollDivHeight &&
    scrollDivHeight > 0
  ) {
    // console.log(
    //   "scroll down...",
    //   distFromTop + elementHeight - scrollDivHeight
    // );
    // scroll element smoothly into view
    scrollDiv.scrollTo({
      top: distFromTop + elementHeight - scrollDivHeight,
      behavior: "smooth",
    });
    // out of view (below)
  } else if (distFromTop < scrollDist) {
    // console.log("scroll up...", distFromTop);

    scrollDiv.scrollTo({
      top: distFromTop,
      behavior: "smooth",
    });
  }
};

// Display -.- if a skin thickness or SWS value is null for any reason
export function handleNullValues(value) {
  if (value === null || value === undefined) {
    return "-.-";
  } else {
    return value.toFixed(decimalPoints);
  }
}

export function calculateSwsColor(swsValue, domainLimits) {
  let colorValue = null;
  let domainRange = [];

  const size = 11;
  let step = (domainLimits[1] - domainLimits[0]) / (size - 1);
  for (var i = 0; i < size; i++) {
    domainRange.push(domainLimits[0] + step * i);
  }

  if (swsValue !== null) {
    const calculateLinear = scaleLinear()
      .domain(domainRange)
      .range([
        colors.swsPlotColorStop10,
        colors.swsPlotColorStop9,
        colors.swsPlotColorStop8,
        colors.swsPlotColorStop7,
        colors.swsPlotColorStop6,
        colors.swsPlotColorStop5,
        colors.swsPlotColorStop4,
        colors.swsPlotColorStop3,
        colors.swsPlotColorStop2,
        colors.swsPlotColorStop1,
        colors.swsPlotColorStop0,
      ]);
    colorValue = calculateLinear(swsValue);
  }
  return colorValue;
}

export function calculateThicknessColor(thicknessValue, domainLimits) {
  let colorValue = null;
  let domainRange = [];

  const size = 11;
  let step = (domainLimits[1] - domainLimits[0]) / (size - 1);
  for (var i = 0; i < size; i++) {
    domainRange.push(domainLimits[0] + step * i);
  }

  if (thicknessValue !== null) {
    const calculateLinear = scaleLinear()
      .domain(domainRange)
      .range([
        colors.swsPlotColorStop10,
        colors.swsPlotColorStop9,
        colors.swsPlotColorStop8,
        colors.swsPlotColorStop7,
        colors.swsPlotColorStop6,
        colors.swsPlotColorStop5,
        colors.swsPlotColorStop4,
        colors.swsPlotColorStop3,
        colors.swsPlotColorStop2,
        colors.swsPlotColorStop1,
        colors.swsPlotColorStop0,
      ]);
    colorValue = calculateLinear(thicknessValue);
  }

  return colorValue;
}

// Generate study unique ID and the start time stamp
export function generateStudyIdAndTimeStamp() {
  let date = new Date();
  let studyStamps = {};
  const epochString = date.getTime().toString(); // A different method of creating a study ID can be considered
  studyStamps.id = TimeStamp() + "_" + epochString;
  studyStamps.startedAt =
    date.getFullYear() +
    "-" +
    ("0" + (date.getMonth() + 1)).slice(-2) +
    "-" +
    ("0" + date.getDate()).slice(-2) +
    " " +
    ("0" + date.getHours()).slice(-2) +
    ":" +
    ("0" + date.getMinutes()).slice(-2) +
    ":" +
    ("0" + date.getSeconds()).slice(-2);
  studyStamps.studyDate =
    ("0" + (date.getMonth() + 1)).slice(-2) +
    "/" +
    ("0" + date.getDate()).slice(-2) +
    "/" +
    date.getFullYear();
  return studyStamps;
}
// Time stamp used for the automatic acquisition series title, and for time stamping the study
export function TimeStamp() {
  // Create time stamp
  let today = new Date();
  let dd = today.getDate();
  let mm = today.getMonth() + 1;
  let yyyy = today.getFullYear();
  let HH = today.getHours();
  let MM = today.getMinutes();
  let SS = today.getSeconds();
  if (dd < 10) {
    dd = "0" + dd;
  }
  if (mm < 10) {
    mm = "0" + mm;
  }
  if (HH < 10) {
    HH = "0" + HH;
  }
  if (MM < 10) {
    MM = "0" + MM;
  }
  if (SS < 10) {
    SS = "0" + SS;
  }
  today = yyyy + "_" + mm + "_" + dd + "_" + HH + "_" + MM + "_" + SS;
  return today;
}

// Determine image file type. Used when uploading custom illustration images
export function getImageType(base64) {
  let imageType = ".jpg";
  if (base64.startsWith("data:image/jpeg;base64,")) {
    imageType = ".jpg";
  } else if (base64.startsWith("data:image/png;base64,")) {
    imageType = ".png";
  }

  return imageType;
}

// Get the appropriate cloud vs local status icon for the study
export function getCloudStatusIcon(status) {
  switch (status) {
    case remoteStatus.sync: {
      return (
        <Tooltip placement="top" title="Only On Cloud" enterTouchDelay={300}>
          <CloudIcon />
        </Tooltip>
      );

      break;
    }
    case remoteStatus.match: {
      return (
        <Tooltip
          placement="top"
          title="Synced With Cloud"
          enterTouchDelay={300}
        >
          <div>
            <CloudDoubleIconDone />
          </div>
        </Tooltip>
      );
      break;
    }
    case remoteStatus.remoteVersion: {
      return (
        <Tooltip
          placement="top"
          title="Newer Version On Cloud"
          enterTouchDelay={300}
        >
          <div>
            <CloudDoubleIconCloudVersion />
          </div>
        </Tooltip>
      );
      break;
    }
    case remoteStatus.localVersion: {
      return (
        <Tooltip
          placement="top"
          title="Newer Version On Device"
          enterTouchDelay={300}
        >
          <div>
            <CloudDoubleIconLocalVersion />
          </div>
        </Tooltip>
      );
      break;
    }
    case remoteStatus.divergent: {
      return (
        <Tooltip
          placement="top"
          title="Divergent Local And Cloud Versions"
          enterTouchDelay={300}
        >
          <div>
            <CloudDoubleIconWarning />
          </div>
        </Tooltip>
      );
    }
    case remoteStatus.local: {
      return (
        <Tooltip placement="top" title="Only On Device" enterTouchDelay={300}>
          <ComputerIcon />
        </Tooltip>
      );
    }
    default:
      return <CloudIcon />;
  }
}

// Table Functions: filter/sort functions that are common to all data tables (load study, review study, sync to cloud, export to USB, etc)
